import { useState } from "react";
import { toast } from "sonner";
import { useKinCalendarFeed, type KinCalendarFeed } from "@/hooks/useKinCalendarFeed";
import { openExternal } from "@/lib/openExternal";
import { isMedianShellUA } from "@/lib/platform";

/**
 * The FIRST half of the Apple Calendar row in Settings (#1647): Kin → Apple.
 * The second half, AppleCalendarConnect, is Apple → Kin and predates this.
 *
 * Two directions because Apple forces it (no write API), one card because she
 * should not have to understand that. Every label here says what happens in
 * plain words; "subscription", "feed", "ICS" and "webcal" never appear.
 *
 * `KinCalendarFeedPanel` is the presentational half and takes the hook's
 * state as a prop. Settings calls useKinCalendarFeed ONCE and hands the same
 * instance to the row summary and to this panel, so "Showing ✓" in the header
 * and the panel body can never disagree (Copilot on #1648: two instances held
 * two copies of `row`, and the summary went stale until a remount).
 * `KinCalendarFeedConnect` is the self-contained form for any host that has
 * no summary to keep in step.
 */
export const KinCalendarFeedPanel = ({ feed }: { feed: KinCalendarFeed }) => {
  const { loading, active, since, subscribeUrl, enable, disable } = feed;
  const [busy, setBusy] = useState(false);

  const handleEnable = async () => {
    if (busy) return;
    setBusy(true);
    const result = await enable();
    setBusy(false);
    if (!result.ok) {
      toast.error("Hmm, that didn't save. Give it another tap? 💜");
      return;
    }
    // Inside the Median shell the bridge is a native call — no popup blocker,
    // no user-activation deadline — so the subscribe sheet opens right away.
    // On the web, a window.open() this long after her tap would be blocked,
    // so the panel below renders a real link and the toast points at it.
    if (isMedianShellUA(navigator.userAgent)) {
      openExternal(result.url);
      toast("Your phone should ask to subscribe to \"Kin\" — tap Subscribe and you're set 💜");
    } else {
      toast("Almost there — tap Open in Calendar below and say yes to \"Kin\" 💜");
    }
  };

  const handleDisable = async () => {
    if (busy) return;
    setBusy(true);
    const result = await disable();
    setBusy(false);
    if (!result.ok) {
      toast.error("Hmm, that didn't go through. Try again? 💜");
      return;
    }
    toast("Kin stopped sharing to Apple. On your phone, remove the Kin calendar under Calendars to finish.");
  };

  const handleCopy = async () => {
    if (!subscribeUrl) return;
    try {
      await navigator.clipboard.writeText(subscribeUrl);
      toast("Link copied 💜 Paste it into any calendar app that can follow a calendar link.");
    } catch {
      toast.error("Couldn't copy on this device — long-press the link instead.");
    }
  };

  const sinceLabel = since
    ? new Date(since).toLocaleDateString("en-US", { month: "short", day: "numeric" })
    : null;

  return (
    <div data-testid="kin-feed-connect" className="mb-4">
      <p className="font-body font-semibold" style={{ fontSize: 14, color: "#1C1A3E" }}>
        Show Kin on your Apple Calendar
      </p>
      <p className="font-body mt-1" style={{ fontSize: 13, color: "#54525C", lineHeight: 1.5 }}>
        Everything on your Kin schedule shows up in a "Kin" calendar on your phone — imports,
        appointments, all of it. One tap, once.
      </p>

      {loading ? null : !active ? (
        <button
          type="button"
          onClick={handleEnable}
          disabled={busy}
          className="w-full font-body font-semibold text-white mt-3 disabled:opacity-50"
          style={{ height: 44, borderRadius: 12, backgroundColor: "#5050C8", fontSize: 14 }}
        >
          {busy ? "One sec..." : "Add Kin to Apple Calendar"}
        </button>
      ) : (
        <div className="mt-3">
          <p className="font-body font-semibold" style={{ fontSize: 13, color: "#16a34a" }}>
            Showing on Apple{sinceLabel ? ` since ${sinceLabel}` : ""} ✓
          </p>
          <p className="font-body mt-1" style={{ fontSize: 12, color: "#54525C", lineHeight: 1.5 }}>
            Apple checks for new events on its own schedule. To see them right away, open
            Calendar, tap Calendars, and pull down to refresh.
          </p>
          {/* A plain link, her own tap: the only way to open the subscribe
              sheet that works everywhere. Also how she re-opens it if she
              dismissed the sheet the first time. */}
          {subscribeUrl && (
            <a
              href={subscribeUrl}
              data-testid="kin-feed-open-link"
              className="mt-2 w-full inline-block text-center py-3 rounded-xl font-body font-semibold text-sm text-white no-underline"
              style={{ backgroundColor: "#5050C8" }}
            >
              Open in Calendar
            </a>
          )}
          <div className="flex gap-4 mt-2">
            <button
              type="button"
              onClick={handleCopy}
              className="font-body font-semibold text-sm hover:underline"
              style={{ color: "#5050C8" }}
            >
              Copy link
            </button>
            <button
              type="button"
              onClick={handleDisable}
              disabled={busy}
              className="font-body font-semibold text-sm hover:underline disabled:opacity-50"
              style={{ color: "#CC3333" }}
            >
              Stop showing
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const KinCalendarFeedConnect = () => {
  const feed = useKinCalendarFeed();
  return <KinCalendarFeedPanel feed={feed} />;
};

export default KinCalendarFeedConnect;
